import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';

interface PayerUploadHelpDialogProps {
  open: boolean;
  onClose: () => void;
}

export const PayerUploadHelpDialog: React.FC<PayerUploadHelpDialogProps> = ({
  open,
  onClose
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 2 
      }}>
        <Typography variant="h6">
          Payer Upload File Specification
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Stack spacing={3}>
          {/* File Format Requirements */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              File Format Requirements
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload a CSV file containing payer information. The file must include specific headers and follow the data format requirements below.
            </Alert>
          </Box>

          {/* Required Headers */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              CSV Headers (Required)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your CSV file must contain the following column headers in any order:
            </Typography>
            <Table size="small" sx={{ border: 1, borderColor: 'divider' }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Header Name</strong></TableCell>
                  <TableCell><strong>Required</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell><strong>Example</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><code>CenterId</code></TableCell>
                  <TableCell><Chip label="Required" color="error" size="small" /></TableCell>
                  <TableCell>Center identifier (without company prefix)</TableCell>
                  <TableCell>H01, H02, ASC01</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>PayerCode</code></TableCell>
                  <TableCell><Chip label="Required" color="error" size="small" /></TableCell>
                  <TableCell>Unique payer code/identifier</TableCell>
                  <TableCell>BC001, AETNA, MEDICARE</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>PayerName</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Display name for the payer</TableCell>
                  <TableCell>Blue Cross, Aetna, Medicare</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>FinancialClass</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Financial classification category</TableCell>
                  <TableCell>Commercial, Government, Self-Pay</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Portal</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Online portal or website URL</TableCell>
                  <TableCell>portal.bluecross.com, aetna.com</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Phone</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Contact phone number</TableCell>
                  <TableCell>(800) 555-1234, 800-555-1234</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>EcsPayerId</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>External system payer identifier (any format)</TableCell>
                  <TableCell>SKCA0, SB690, Z95754, 12345</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>EvaEnabled</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Whether payer is enabled for EVA worklist filtering</TableCell>
                  <TableCell>true, false, yes, no, 1, 0</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>PayerGroup</code></TableCell>
                  <TableCell><Chip label="Conditional" color="warning" size="small" /></TableCell>
                  <TableCell>EVA payer routing group (required when EvaEnabled is true)</TableCell>
                  <TableCell>BCBS Texas, Medicare, UHC Advantage, Not in Scope</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Company</code></TableCell>
                  <TableCell><Chip label="Optional" color="success" size="small" /></TableCell>
                  <TableCell>Company identifier (will use current if not provided)</TableCell>
                  <TableCell>PSN, PARSEC</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {/* Example CSV */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Example CSV Content
            </Typography>
            <Box sx={{ 
              bgcolor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              overflow: 'auto'
            }}>
              <pre style={{ margin: 0 }}>
{`CenterId,PayerCode,PayerName,FinancialClass,Portal,Phone,EcsPayerId,EvaEnabled,PayerGroup,Company
H01,BC001,Blue Cross Blue Shield,Commercial,portal.bcbs.com,(800) 555-0001,SKCA0,true,BCBS Texas,PSN
H01,AETNA,Aetna Better Health,Commercial,aetna.com,800-555-0002,SB690,false,,
H02,MEDICARE,Medicare,Government,,800-MEDICARE,Z95754,true,Medicare,PARSEC`}
              </pre>
            </Box>
          </Box>

          {/* Important Notes */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              Important Notes
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Data Processing:</strong>
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Empty rows are automatically skipped</li>
                <li>Leading and trailing spaces are automatically trimmed</li>
                <li>If Company is not provided, the current company context will be used</li>
                <li>CenterId will be prefixed with the company (e.g., "H01" becomes "PSN-H01")</li>
                <li>EcsPayerId can be alphanumeric and is not required to be numeric</li>
                <li>If EvaEnabled is true, PayerGroup must be provided and match a supported EVA payer group</li>
                <li>Existing payers with the same PayerCode will be updated</li>
                <li>New payers will be created if the PayerCode doesn't exist</li>
              </ul>
            </Alert>

            <Alert severity="error">
              <Typography variant="body2">
                <strong>Common Upload Errors:</strong>
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Missing required headers (CenterId, PayerCode)</li>
                <li>Empty CenterId or PayerCode values</li>
                <li>Malformed CSV structure (extra commas, unclosed quotes)</li>
                <li>Invalid character encoding (use UTF-8)</li>
              </ul>
            </Alert>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayerUploadHelpDialog;